// apiFetch: a single source of truth for authenticated API calls.
//
// Design goals:
//  1. Always uses relative URLs and credentials:'include' so the access_token
//     cookie is sent on every request.
//  2. Transparently refreshes the access_token (via /api/auth/refresh) and
//     retries ONCE when a request returns 401, so short-lived JWT tokens don't
//     surface as user-visible errors mid-session.
//  3. Coordinates concurrent 401s through a SINGLE in-flight refresh promise —
//     ten parallel requests all blowing up at once still trigger exactly one
//     /api/auth/refresh call, then retry once the new token is live.
//  4. `skipAuthRetry: true` opts out of retry — required for the auth endpoints
//     themselves (login/register/logout/refresh/me) to avoid recursion loops.
//
// Return shape mirrors axios-lite: { data, status, response }.
// On non-2xx (after any retry) throws an Error with `.response = {status, data}`
// so existing axios-style catch blocks (`err.response?.data?.detail`) work unchanged.

// Module-level dedup of refresh attempts. If a refresh is already in flight, all
// other callers await the same promise instead of firing parallel refreshes.
let _refreshInFlight = null;

function _resetRefresh() { _refreshInFlight = null; }

function _coordinateRefresh(refreshFn) {
  if (!_refreshInFlight) {
    _refreshInFlight = (async () => {
      try {
        return await refreshFn();
      } finally {
        // Always clear so the next 401 cycle can refresh again.
        // Use a microtask delay so concurrent awaiters all read the same result first.
        Promise.resolve().then(_resetRefresh);
      }
    })();
  }
  return _refreshInFlight;
}

// Default refresh implementation — calls POST /api/auth/refresh.
// Override via apiFetch.setRefreshFn() if AuthContext wants different behavior.
let _refreshFn = async () => {
  const r = await fetch('/api/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Accept': 'application/json' },
  });
  return r.ok;
};

export function setRefreshFn(fn) {
  _refreshFn = fn;
}

// Optional callback fired when refresh fails (so AuthContext can clear user state).
let _onAuthFailure = null;
export function setOnAuthFailure(fn) {
  _onAuthFailure = fn;
}

async function _readBody(response) {
  // Best-effort body read — never throws.
  const ct = response.headers.get('content-type') || '';
  try {
    if (ct.includes('application/json')) return await response.json();
    const text = await response.text();
    return text || null;
  } catch {
    return null;
  }
}

function _buildError(status, data) {
  // Construct an axios-shaped error so existing catch blocks keep working.
  const detail = (data && typeof data === 'object' && data.detail) || `Request failed with status ${status}`;
  const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
  const err = new Error(msg);
  err.response = { status, data };
  return err;
}

/**
 * apiFetch(path, options?)
 *
 * @param {string} path - URL path. Should start with "/" (relative) to guarantee same-origin cookies.
 *                        Absolute URLs are passed through unchanged.
 * @param {object} [options]
 * @param {string} [options.method='GET']
 * @param {object|string} [options.body] - Object is JSON-stringified automatically.
 * @param {object} [options.headers]
 * @param {boolean} [options.skipAuthRetry=false] - If true, no 401 retry-with-refresh.
 * @param {AbortSignal} [options.signal]
 *
 * @returns {Promise<{data: any, status: number, response: Response}>}
 * @throws {Error} with `.response = {status, data}` on non-2xx (after any retry).
 */
export async function apiFetch(path, options = {}) {
  const {
    method = 'GET',
    body,
    headers = {},
    skipAuthRetry = false,
    signal,
    ...rest
  } = options;

  const init = {
    method,
    credentials: 'include',
    headers: {
      'Accept': 'application/json',
      ...(body !== undefined && !(body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    signal,
    ...rest,
  };
  if (body !== undefined) {
    init.body = body instanceof FormData || typeof body === 'string' ? body : JSON.stringify(body);
  }

  const doFetch = () => fetch(path, init);

  let response = await doFetch();

  if (response.status === 401 && !skipAuthRetry) {
    // Coordinate ONE refresh across all concurrent 401s
    const refreshed = await _coordinateRefresh(_refreshFn);
    if (refreshed) {
      response = await doFetch();
    } else if (_onAuthFailure) {
      try { _onAuthFailure(); } catch { /* swallow — best-effort cleanup */ }
    }
  }

  const data = await _readBody(response);

  if (response.status >= 200 && response.status < 300) {
    return { data, status: response.status, response };
  }
  throw _buildError(response.status, data);
}

// Internal export for tests only
export const __internal = {
  resetRefresh: _resetRefresh,
  getRefreshInFlight: () => _refreshInFlight,
};
